import React from "react";
import { motion } from "framer-motion";

const problems = [
  {
    title: "Diabetes develops in silence.",
    body: "By the time HbA1c crosses the diagnostic line, the metabolic damage is years old — invisible on a single annual test.",
    icon: "flatline",
    accent: "coral",
  },
  {
    title: "Burnout accumulates before you notice.",
    body: "HRV drops, sleep quality slips, mood flattens — a compounding pattern most apps see one signal at a time.",
    icon: "wave",
    accent: "lav",
  },
  {
    title: "Emotional eating goes unnoticed.",
    body: "Stress, loneliness and boredom quietly rewrite your food choices. No calorie tracker sees the *why*.",
    icon: "heart-crack",
    accent: "coral",
  },
  {
    title: "Biomarker changes get ignored.",
    body: "Years of blood reports sitting in emails. The drift that matters lives between two numbers, not on one.",
    icon: "chart-drop",
    accent: "teal",
  },
];

const Ic = ({ name, color }) => {
  const c = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  const m = {
    flatline: <svg {...c}><path d="M3 12h5l1-4 2 8 2-6 2 4h6" /></svg>,
    wave: <svg {...c}><path d="M3 12c3-4 5 4 8 0s5 4 8 0 2-3 2-3" /></svg>,
    "heart-crack": <svg {...c}><path d="M12 21s-8-5.5-8-11a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 5.5-8 11-8 11z" /><path d="M12 9l-2 3h4l-2 3" /></svg>,
    "chart-drop": <svg {...c}><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 6-8" /></svg>,
  };
  return m[name];
};

const accentMap = {
  teal: { color: "#2DD4BF", chip: "text-teal-soft border-teal-mid/40 bg-teal-mid/[0.06]", border: "hover:border-teal-mid/40" },
  lav: { color: "#C4B5FD", chip: "text-lav-400 border-lav-500/40 bg-lav-500/[0.06]", border: "hover:border-lav-500/40" },
  coral: { color: "#FDA4AF", chip: "text-coral-400 border-coral-500/40 bg-coral-500/[0.06]", border: "hover:border-coral-500/40" },
};

export default function Problem() {
  return (
    <section id="problem" data-testid="problem-section" className="relative py-24 md:py-32">
      <div className="absolute inset-0 -z-10 bg-grid opacity-[0.22]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-teal-soft">02 — The Problem</div>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter leading-[1.05] font-medium">
            Healthcare <span className="text-zinc-500 line-through decoration-coral-500/70">reacts</span> too late.
            <br /><span className="gradient-text-teal-lav">Your body has been trying to tell you for months.</span>
          </h2>
          <p className="mt-5 text-zinc-400 max-w-2xl">
            Every chronic condition of the last century started as a whisper of numbers, mood and behavior. HealthIRL listens to that whisper.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {problems.map((p, i) => {
            const a = accentMap[p.accent];
            return (
              <motion.div key={p.title}
                initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-8% 0px" }} transition={{ delay: i * 0.06, duration: 0.6 }}
                data-testid={`problem-card-${i}`}
                className={`group relative rounded-2xl border border-white/[0.07] bg-ink-800/60 p-7 overflow-hidden transition-all hover:-translate-y-1 ${a.border}`}>
                <div className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-wider ${a.chip}`}>
                  <Ic name={p.icon} color={a.color} />
                  Signal {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-5 font-heading text-2xl tracking-tight leading-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
